import { FC, useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { membership, type MembershipMeResponse } from '../services/api';
import {
  getMembershipRedirectPath,
  isPathAllowedForMember,
} from '../utils/membershipRedirect';
import Loading from './Loading';

const MembershipOutletGuard: FC = () => {
  const location = useLocation();
  const [membershipMe, setMembershipMe] = useState<MembershipMeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const lastAllowedPathRef = useRef(getMembershipRedirectPath());

  useEffect(() => {
    let cancelled = false;

    const fetchMembership = async () => {
      setLoading(true);
      try {
        const response = await membership.getMe();
        if (!cancelled) {
          setMembershipMe(response.data);
          lastAllowedPathRef.current = getMembershipRedirectPath(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch membership info:', error);
        if (!cancelled) {
          setMembershipMe({ is_member: false, ui_modules: [] });
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchMembership();
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <Loading />;
  }

  if (!membershipMe?.is_member) {
    lastAllowedPathRef.current = location.pathname;
    return <Outlet />;
  }

  const uiModules = membershipMe.ui_modules ?? [];

  if (location.pathname === '/' || isPathAllowedForMember(location.pathname, uiModules)) {
    lastAllowedPathRef.current = location.pathname === '/'
      ? getMembershipRedirectPath(membershipMe)
      : location.pathname;
    return <Outlet />;
  }

  const fallbackPath =
    lastAllowedPathRef.current !== location.pathname
      ? lastAllowedPathRef.current
      : getMembershipRedirectPath(membershipMe);

  return <Navigate to={fallbackPath} replace />;
};

export default MembershipOutletGuard;
